import { useContext, useEffect, useState } from 'react';

import { Field, ImageField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { WildCardRouteContext } from 'src/context/WildCardRoute/WildCardRouteContext';

import { ComponentProps, SitecoreContextValue } from 'src/lib/ComponentProps';

import { SocialSharingOptions } from 'src/types/SocialSharingOptions';

import { FieldEditFrame } from 'src/utils/customFieldEditFrame';
import { isResourceContentPage, isResourcePage } from 'src/utils/isTemplate';

import { Button } from 'src/components/basicContent/_Button/Button';
import { EditorHelp } from 'src/components/layout/_EditorHelp/_EditorHelp';

import ContentOverviewHeaderContainer from '../_contentOverviewHeaderContainer/ContentOverviewHeaderContainer';

import ContentOverviewRelatedResourcesCoursesCard from './ContentOverviewRelatedResourcesCoursesCard/ContentOverviewRelatedResourcesCoursesCard';

import styles from './ContentOverviewRelatedResourcesCourses.module.css';
export type ShareContentFields = {
	ShareButtonToolTip: Field<string>;
	ShareOptions: SocialSharingOptions;
	NoShareOptions: Field<string>;
	FavouriteButtonText: Field<string>;
	UnfavouriteText: Field<string>;
};

export type RelatedContentImageFields = {
	value: {
		alt: string;
		src: string;
	};
};

export type RelatedContentFields = {
	fields?: {
		Image: RelatedContentImageFields;
		Title: Field<string>;
		PageTitle: Field<string>;
		'Background Image': ImageField;
		PageImage: RelatedContentImageFields;
	};
	id: string;
	name: string;
	title: string;
};

type ContentOverviewRelatedResourcesCoursesProps = ComponentProps & {
	fields: ShareContentFields & {
		DatasourceId: string;
		RelatedResourcesTitle: Field<string>;
		PageTitle: Field<string>;
		RelatedCoursesText: Field<string>;
		RelatedCourses: RelatedContentFields[];
		HideRelatedCoursesTab: Field<boolean>;
		RelatedResourcesText: Field<string>;
		RelatedResources: RelatedContentFields[];
		HideRelatedResourcesTab: Field<boolean>;
	};
};

enum Tabs {
	'RelatedCourses',
	'RelatedResources',
}

export default function ContentOverviewRelatedResourcesCourses(props: ContentOverviewRelatedResourcesCoursesProps): JSX.Element {
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const displayRelatedCoursesTab = !props.fields?.HideRelatedCoursesTab?.value;
	const displayRelatedResourcesTab = !props.fields?.HideRelatedResourcesTab?.value;
	const WildCardRoute = useContext(WildCardRouteContext);
	const isResourcePageTemplate =
		isResourcePage(WildCardRoute?.wildCardItemInfo?.TemplateId) || isResourceContentPage(WildCardRoute?.wildCardItemInfo?.TemplateId);

	const [selectedTab, setSelectedTab] = useState<Tabs>(displayRelatedCoursesTab ? Tabs.RelatedCourses : Tabs.RelatedResources);

	useEffect(() => {
		setSelectedTab(isResourcePageTemplate ? Tabs.RelatedResources : Tabs.RelatedCourses);
	}, [isResourcePageTemplate]);

	if (
		!props.fields ||
		(!props.fields.RelatedCourses && !props.fields.RelatedResources) ||
		(props.fields.RelatedCourses.length < 1 && props.fields.RelatedResources.length < 1) ||
		(!displayRelatedCoursesTab && !displayRelatedResourcesTab)
	) {
		return <></>;
	}

	const ContentOverviewRelatedResourcesCourseListButton = (text: string, tab: Tabs) => {
		return (
			<Button
				title={text}
				className={`buttonReset ${styles.contentOverviewRelatedResourcesCourseItem} ${selectedTab === tab ? styles.active : ''}`}
				onClick={() => setSelectedTab(tab)}
			>
				<Text field={{ value: text }} />
			</Button>
		);
	};

	const EmptyRelatedListContainer = (textField: Field<string>) => {
		return (
			<div className={styles.noRelatedResourcesCourses}>
				<Text field={textField} />
			</div>
		);
	};

	return (
		<section className={`${styles.contentOverviewRelatedResourcesCoursesContainer} pad maxWidth`}>
			<FieldEditFrame
				itemId={props.fields?.DatasourceId ?? undefined}
				fields={['HideRelatedCoursesTab', 'RelatedCourses', 'HideRelatedResourcesTab', 'RelatedResources']}
				header="Edit related resources and courses"
			>
				<EditorHelp text="Click here to edit related courses and resources" />
				<ContentOverviewHeaderContainer header={sitecoreContext.dictionary?.relatedCoursesAndResources} />
				<div className={styles.contentOverviewRelatedResourcesCoursesWrapper}>
					<div className={styles.contentOverviewRelatedResourcesCourseListContainer}>
						{displayRelatedCoursesTab &&
							ContentOverviewRelatedResourcesCourseListButton(sitecoreContext.dictionary?.RelatedCourses, Tabs.RelatedCourses)}
						{displayRelatedResourcesTab &&
							ContentOverviewRelatedResourcesCourseListButton(sitecoreContext.dictionary?.RelatedResources, Tabs.RelatedResources)}
					</div>
					<div
						className={`${styles.relatedListContainer} ${
							selectedTab === Tabs.RelatedCourses
								? !(props.fields?.RelatedCourses ? props.fields?.RelatedCourses.length : 0)
									? styles.emptyRelatedListContainer
									: ''
								: !(props.fields?.RelatedResources ? props.fields?.RelatedResources.length : 0)
								? styles.emptyRelatedListContainer
								: ''
						}`}
					>
						{displayRelatedCoursesTab && selectedTab === Tabs.RelatedCourses && (
							<>
								{props.fields?.RelatedCourses?.length > 0
									? props.fields?.RelatedCourses.map(
											(relatedCourse, index) =>
												index < 4 && (
													<ContentOverviewRelatedResourcesCoursesCard key={index} relatedContent={relatedCourse} {...props.fields} />
												)
									  )
									: EmptyRelatedListContainer(props.fields?.RelatedCoursesText)}
							</>
						)}
						{displayRelatedResourcesTab && selectedTab === Tabs.RelatedResources && (
							<>
								{props.fields?.RelatedResources?.length > 0
									? props.fields?.RelatedResources.map(
											(relatedResource, index) =>
												index < 4 && (
													<ContentOverviewRelatedResourcesCoursesCard key={index} relatedContent={relatedResource} {...props.fields} />
												)
									  )
									: EmptyRelatedListContainer(props.fields?.RelatedResourcesText)}
							</>
						)}
					</div>
				</div>
			</FieldEditFrame>
		</section>
	);
}
